import { Button as ChakraButton, ButtonProps as ChakraButtonProps } from "@chakra-ui/react";
import type { SystemStyleObject } from "@chakra-ui/react";

export type ButtonProps = {
  variant?: "primary" | "secondary" | "ghost" | "link" | "danger";
  size?: "xs" | "sm" | "md" | "lg";
  isFullWidth?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
} & Omit<ChakraButtonProps, "variant" | "size" | "h" | "w" | "height" | "width">;

export const baseStyle: SystemStyleObject = {
  fontWeight: 700,
  borderRadius: "sm",
  cursor: "pointer",
  whiteSpace: "normal",
  _disabled: {
    opacity: 0.4,
    backgroundColor: "grey.500",
    color: "white",
    cursor: "not-allowed",
  },
};

const primaryStyle: SystemStyleObject = {
  color: "white",
  backgroundColor: "pinkFlink.500",
  _loading: {
    color: "white",
    backgroundColor: "pinkFlink.500",
  },
  _active: {
    backgroundColor: "pinkFlink.400",
  },
  _hover: {
    backgroundColor: "pinkFlink.400",
  },
};
const secondaryStyle: SystemStyleObject = {
  color: "pinkFlink.500",
  backgroundColor: "pinkFlink.100",
  _loading: {
    color: "pinkFlink.500",
    backgroundColor: "pinkFlink.200",
  },
  _active: {
    backgroundColor: "pinkFlink.200",
  },
  _hover: {
    backgroundColor: "pinkFlink.200",
  },
};

const ghostStyle: SystemStyleObject = {
  backgroundColor: "transparent",
  color: "grey.800",
  _hover: {
    backgroundColor: "grey.100",
  },
  _active: {
    backgroundColor: "grey.200",
  },
};

const linkStyle: SystemStyleObject = {
  color: "grey.800",
  backgroundColor: "transparent",
  textDecoration: "underline",
  _hover: {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
  _active: {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
};

const dangerStyle: SystemStyleObject = {
  color: "white",
  backgroundColor: "red.500",
  _loading: {
    color: "white",
    backgroundColor: "red.500",
  },
  _active: {
    backgroundColor: "red.400",
  },
  _hover: {
    backgroundColor: "red.400",
  },
};

const sizeStyle: Record<string, SystemStyleObject> = {
  lg: {
    h: 12,
    minW: 12,
    fontSize: "md",
    px: 6,
  },
  md: {
    h: 10,
    minW: 10,
    fontSize: "sm",
    px: 4,
  },
  sm: {
    h: 8,
    minW: 8,
    fontSize: "sm",
    px: 3,
  },
  xs: {
    h: 6,
    minW: 6,
    fontSize: "xs",
    px: 2,
  },
};

const getButtonStyle = (variant: string): SystemStyleObject => {
  switch (variant) {
    case "primary":
      return primaryStyle;
    case "secondary":
      return secondaryStyle;
    case "ghost":
      return ghostStyle;
    case "link":
      return linkStyle;
    case "danger":
      return dangerStyle;
    default:
      return primaryStyle;
  }
};

const getSizeStyle = (size: string): SystemStyleObject => {
  return sizeStyle[size] || sizeStyle["md"];
};

export function Button({
  children,
  variant = "primary",
  size = "md",
  isFullWidth = false,
  isLoading = false,
  isDisabled = false,
  ...rest
}: ButtonProps) {
  const buttonStyle = {
    ...baseStyle,
    ...getSizeStyle(size),
    ...getButtonStyle(variant),
  };
  return (
    <ChakraButton
      {...(buttonStyle as ChakraButtonProps)}
      size={size}
      isLoading={isLoading}
      isDisabled={isDisabled}
      aria-label={typeof children === "string" ? children : undefined}
      width={isFullWidth ? "full" : "auto"}
      {...rest}
    >
      {children}
    </ChakraButton>
  );
}
